import { useContext, useEffect } from 'react';
import {
  Path,
  To,
  UNSAFE_NavigationContext,
  useLocation,
} from 'react-router-dom';

export const ACQUISITION_UNSAVED_WORK_MESSAGE =
  'Hai un’acquisizione non conclusa su questa pagina. Se la lasci, i dati compilati e le operazioni da completare andranno persi. Vuoi uscire comunque?';

/** Where a navigation is headed, with the query and the fragment cut off. */
const pathnameOf = (to: To, current: string) => {
  const target: Partial<Path> = typeof to === 'string' ? { pathname: to } : to;
  const pathname = target.pathname;
  // A search-only or hash-only destination stays where it is; react-router
  // resolves it before it reaches here, but a caller may also omit it.
  if (!pathname) return current;
  return pathname.split('?')[0].split('#')[0];
};

/**
 * Asks before an unfinished acquisition is abandoned.
 *
 * The workspace holds compiled drafts and operations that are not resolved yet,
 * and none of it survives leaving the page: the drafts live above the panels
 * but inside the page, and what a reload can restore is only an operation's
 * identity, never the command. So both ways out are questioned — the browser's
 * own unload, and a route change inside the app.
 *
 * Changing product tab is deliberately not one of those ways out. It is the
 * same route with another `prodotto`, and the whole point of the session store
 * is that it costs nothing, so a destination on the current path passes
 * through untouched.
 *
 * The in-app half works by wrapping the router's navigator while there is
 * something to lose. `useBlocker` would be the supported way to do this, but it
 * exists only under a data router and this app mounts a `BrowserRouter`;
 * wrapping is scoped to this page, reversed on cleanup, and leaves `go`
 * untouched, because Back and Forward cannot be held open for an answer.
 */
export const useAcquisitionNavigationGuard = (hasUnsavedWork: boolean) => {
  const { navigator } = useContext(UNSAFE_NavigationContext);
  const { pathname } = useLocation();

  useEffect(() => {
    if (!hasUnsavedWork) return undefined;

    const warn = (event: BeforeUnloadEvent) => {
      // The browser shows its own wording; what it needs is the refusal to
      // leave quietly, which these two lines say across engines.
      event.preventDefault();
      event.returnValue = '';
      return '';
    };

    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [hasUnsavedWork]);

  useEffect(() => {
    if (!hasUnsavedWork) return undefined;

    const { push, replace } = navigator;

    const guarded =
      (navigate: typeof push) =>
      (to: To, ...rest: unknown[]) => {
        if (
          pathnameOf(to, pathname) !== pathname &&
          !window.confirm(ACQUISITION_UNSAVED_WORK_MESSAGE)
        ) {
          return;
        }

        (navigate as (to: To, ...args: unknown[]) => void)(to, ...rest);
      };

    navigator.push = guarded(push);
    navigator.replace = guarded(replace);

    return () => {
      navigator.push = push;
      navigator.replace = replace;
    };
  }, [hasUnsavedWork, navigator, pathname]);
};
